// src/components/Admin/CategoryGroup/CategoryGroupCard.jsx
//
import React, { useState } from 'react';
import { deleteCategoryGroup } from '../../../../services/categoryGroupApis';
import ConfirmationModal from './ConfirmationModal'; 
import { toProperCase } from '../../../../utility/textFormatting';
import AppIconRenderer from '../../../Shared/AppIconRenderer'; // Import AppIconRenderer

const CategoryGroupCard = ({ group, onEdit, onDeleteSuccess, locale}) => {
  const [showAllChildren, setShowAllChildren] = useState(false);
  const childrenToShow = showAllChildren ? group.child_categories : (group.child_categories || []).slice(0, 5);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmDetails, setConfirmDetails] = useState({ action: null, message: '', title: '' });

  const handlePermanentDelete = async () => {
    try {
      // console.log("FRONTEND: Attempting to delete group with ID:", group.id); // Log for delete all bug
      // if (typeof group.id !== 'number' || group.id <= 0) { 
      //     console.error("FRONTEND: Invalid group ID for deletion:", group.id);
      //     alert("Error: Invalid group ID provided for deletion.");
      //     setIsConfirmOpen(false);
      //     return;
      // }
      await deleteCategoryGroup(group.id, locale); 
      onDeleteSuccess();
    } catch (error) {
      console.error("Failed to permanently delete group:", error);
      alert("Error deleting group: " + (error.response?.data?.detail || error.message));
    }
    setIsConfirmOpen(false);
  };
  
  const openDeleteConfirmation = () => {
    let message = `Are you sure you want to PERMANENTLY DELETE the group "${group.name}"? This action cannot be undone and will remove all its associations.`;
    if (group.purposes && group.purposes.length > 1) {
      const purposesString = group.purposes.map(p => toProperCase(p)).join(', ');
      message = `The group "${group.name}" is used for multiple purposes (${purposesString}). Are you sure you want to PERMANENTLY DELETE it? This action cannot be undone and will remove all its associations.`;
    }

    setConfirmDetails({
        action: handlePermanentDelete,
        message: message,
        title: "Confirm Permanent Deletion"
    });
    setIsConfirmOpen(true);
  };

  // Using theme variables from ui-ux-reference.txt for colors where appropriate
  // Assuming Tailwind is configured with these CSS variables, e.g., text-foreground, bg-card, border-border
  // For simplicity, I'll use direct Tailwind classes that align with the intent.

  return (
    <div className="bg-card shadow-md rounded-lg p-5 m-2 w-full max-w-sm flex flex-col border border-border"> {/* */}
      {/* Card Header */}
      <div className="flex justify-between items-start mb-3">
        <h4 className="text-lg font-semibold text-foreground leading-tight flex items-center"> {/* */}
          {group.icon && ( // Display icon if available
            <AppIconRenderer 
              name={group.icon} 
              size={20} // Adjusted size slightly
              className="mr-2 text-primary" // Use primary color for icon
            />
          )}
          {group.name}
        </h4>
        <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full whitespace-nowrap ${
          group.is_active ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive' // Using success/destructive colors
        }`}>
          {group.is_active ? 'Active' : 'Inactive'}
        </span>
      </div>

      {/* Card Body */}
      <div className="flex-grow space-y-3">
        <p className="text-sm text-muted-foreground"> {/* */}
          <span className="font-medium text-foreground">Slug:</span> {group.slug}
        </p>

        {group.description && (
          <div className="text-sm text-foreground"> {/* */}
            <p className="font-medium text-foreground mb-0.5">Description:</p>
            <p className="max-h-20 overflow-y-auto text-muted-foreground leading-snug">{group.description}</p>
          </div>
        )}

        {group.purposes && group.purposes.length > 0 && (
          <div>
            <strong className="text-sm font-medium text-foreground block mb-1">Purposes:</strong> {/* */}
            <div className="flex flex-wrap gap-1.5">
              {group.purposes.map(p => (
                <span key={p} className="text-xs bg-secondary text-secondary-foreground px-2 py-1 rounded-full"> {/* Badge-like with secondary colors */}
                  {toProperCase(p)}
                </span>
              ))}
            </div>
          </div>
        )}

        <div>
          <strong className="text-sm font-medium text-foreground block mb-1"> {/* */}
            Child Categories ({ (group.child_categories || []).length }):
          </strong>
          {(group.child_categories || []).length > 0 ? (
            <>
              <ul className="list-none p-0 space-y-0.5 max-h-24 overflow-y-auto text-sm text-muted-foreground"> {/* */}
                {childrenToShow.map(child => (
                  <li key={child.id}>
                    {typeof child.name === 'string' ? child.name : 'Error: Name is object'} <span className="text-gray-400">(ID: {child.id})</span>
                  </li>
                ))}
              </ul>
              {(group.child_categories || []).length > 5 && (
                <button 
                  onClick={() => setShowAllChildren(!showAllChildren)}
                  className="text-xs text-primary hover:text-primary/80 mt-1.5 p-0 bg-transparent border-none cursor-pointer" // Link-like button
                >
                  {showAllChildren ? 'Show Less' : 'View More'}
                </button>
              )}
            </>
          ) : <p className="text-sm text-muted-foreground italic">No child categories assigned.</p>} {/* */}
        </div>
      </div>

      {/* Card Footer - Actions */}
      <div className="mt-5 pt-4 border-t border-border flex items-center space-x-3"> {/* */}
        <button 
            onClick={onEdit}
            // Using 'outline' or 'secondary' variant for "Edit" as per typical UI patterns
            // Assuming you have buttonVariants or can style similarly
            className="flex-1 px-3 py-2 text-sm font-medium text-center text-primary border border-primary hover:bg-primary/10 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary transition-colors" // Outline style
        >
            Edit
        </button>
        <button 
            onClick={openDeleteConfirmation}
            // Using 'destructive' variant
            className="flex-1 px-3 py-2 text-sm font-medium text-center text-destructive-foreground bg-destructive hover:bg-destructive/90 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-destructive transition-colors"
        >
            Delete
        </button>
      </div>

      <ConfirmationModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={confirmDetails.action}
        title={confirmDetails.title}
      >
        <p className="text-sm text-muted-foreground">{confirmDetails.message}</p> {/* */}
      </ConfirmationModal>
    </div>
  );
};

export default CategoryGroupCard;